import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { finalize } from 'rxjs';
import { PickupFieldOption, PickupFormField } from '../../models/cart.model';
import { ApiService } from '../../services/api.service';
import { ConstantsService } from '../../services/constants.service';
import { DataService } from '../../services/data.service';
import { StorageService } from '../../services/storage.service';
import { StoredItem } from '../../shared/enums';
import { ToastService } from '../../services/toast.service';

export interface OrderFormSheetData {
  fields: PickupFormField[];
  email?: string;
  // Called synchronously from the Confirm tap, so whatever runs next (Apple Pay)
  // is still inside the user gesture - unlike afterDismissed, which fires on animationend.
  onConfirm?: (result: OrderFormResult) => void;
}

export interface OrderFormResult {
  metadata: Record<string, unknown>;
}

// Field keys are venue-configured on the backend, so a name field is recognised by its
// key; phone and email have dedicated field types.
function isIdentityField(field: PickupFormField): boolean {
  return field.type === 'phone' || field.type === 'email' || field.key === 'email' || /name/i.test(field.key);
}

@Component({
  selector: 'app-order-form',
  templateUrl: './order-form.component.html',
  styleUrl: './order-form.component.scss',
  standalone: false,
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', floatLabel: 'auto', subscriptSizing: 'dynamic' } },
  ],
})
export class OrderFormComponent implements OnInit {
  private readonly dataService = inject(DataService);
  private readonly storageService = inject(StorageService);
  private readonly apiService = inject(ApiService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly constants = inject(ConstantsService);
  private readonly data = inject<OrderFormSheetData>(MAT_BOTTOM_SHEET_DATA);
  readonly sheetRef = inject(MatBottomSheetRef<OrderFormComponent, OrderFormResult>);

  readonly fields = this.data.fields;
  formGroup: FormGroup;
  submitted = false;
  loading = false;

  minDate: Date;
  maxDate: Date;

  private requiresRoomCheck = false;
  private disabledDays: number[] = [];
  private disabledMonths: number[] = [];

  ngOnInit(): void {
    this.formGroup = this.buildForm();
    this.resetForJrccTables();
  }

  confirm(): void {
    this.submitted = true;
    if (!this.isFormValid()) return;

    if (this.isSharedDevice()) {
      // Also clears anything saved before this device was treated as shared.
      this.storageService.remove(StoredItem.OrderForm);
    } else {
      this.saveIdentityValues();
    }

    if (this.requiresRoomCheck) {
      this.checkRoomAndClose();
    } else {
      this.close(this.toMetadata());
    }
  }

  close(): void;
  close(metadata: Record<string, unknown>): void;
  close(metadata?: Record<string, unknown>): void {
    if (metadata) this.data.onConfirm?.({ metadata });
    this.sheetRef.dismiss(metadata ? { metadata } : undefined);
  }

  // A plain string/number option is both the submitted value and the label.
  toOption(option: PickupFieldOption): { key: string | number; value: string | number } {
    return typeof option === 'object' && option !== null ? option : { key: option, value: option };
  }

  getFilteredDates =(date: Date | null): boolean => {
    const day = (date || new Date()).getDate();
    const month = (date || new Date()).getMonth();
    return !this.disabledDays.includes(day) && !this.disabledMonths.includes(month);
  };

  private buildForm(): FormGroup {
    const controls: Record<string, FormControl> = {};
    let minDay: number | undefined;
    let maxDay: number | undefined;

    for (const field of this.fields) {
      controls[field.key] = new FormControl('', this.validatorsFor(field));

      if (field.type === 'calendar') {
        if (field.min_day) minDay = field.min_day;
        if (field.max_day) maxDay = field.max_day;
        if (field.disabled_days) this.disabledDays = field.disabled_days;
        if (field.disabled_months) this.disabledMonths = field.disabled_months;
      }
      if (field.key === 'room' && field.backend === 'room_check') {
        this.requiresRoomCheck = true;
      }
    }

    const formGroup = new FormGroup(controls);
    this.applyEmailValidator(formGroup);
    this.applyDateBounds(formGroup, minDay, maxDay);
    this.restorePreviousValues(formGroup);
    return formGroup;
  }

  // Calendar fields hold a JS Date, which JSON-serialises as a UTC timestamp and lands on
  // the previous day for UTC+ venues; the backend expects a plain local YYYY-MM-DD.
  private toMetadata(): Record<string, unknown> {
    const metadata = this.formGroup.getRawValue();
    for (const field of this.fields) {
      const value = metadata[field.key];
      if (field.type === 'calendar' && value) {
        metadata[field.key] = DateTime.fromJSDate(new Date(value)).toISODate();
      }
    }
    return metadata;
  }

  private validatorsFor(field: PickupFormField): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (field.required) {
      validators.push(Validators.required, Validators.minLength(1));
    }
    if (field.pattern) {
      validators.push(Validators.pattern(field.pattern.regex));
    }
    return validators;
  }

  private isFormValid(): boolean {
    return this.fields.every(field => this.formGroup.get(field.key)?.valid);
  }

  private applyEmailValidator(formGroup: FormGroup): void {
    const emailControl = formGroup.controls['email'];
    if (!emailControl) return;

    if (this.data.email) {
      emailControl.setValue(this.data.email);
    }
    emailControl.addValidators(Validators.email);
    emailControl.updateValueAndValidity();
  }

  // The date picker's min/max bounds only apply to a field keyed 'date'.
  private applyDateBounds(formGroup: FormGroup, minDay?: number, maxDay?: number): void {
    if (!formGroup.controls['date']) return;

    this.minDate = minDay ? this.addDays(minDay) : new Date();
    if (maxDay) this.maxDate = this.addDays(maxDay);
  }

  private addDays(days: number): Date {
    return new Date(new Date().setDate(new Date().getDate() + days));
  }

  // Only the guest's identity (name/phone/email) is remembered between orders. Everything
  // else - date, room, guests, etc. - describes a single order, so it's asked fresh each
  // time rather than silently reused stale. The storage key is shared across venues, so
  // this form's non-identity keys are dropped and other venues' keys left untouched.
  private saveIdentityValues(): void {
    const stored = this.readStoredValues();
    const values = this.formGroup.getRawValue();

    for (const field of this.fields) {
      if (isIdentityField(field)) {
        stored[field.key] = values[field.key];
      } else {
        delete stored[field.key];
      }
    }
    this.storageService.set(StoredItem.OrderForm, JSON.stringify(stored));
  }

  private restorePreviousValues(formGroup: FormGroup): void {
    if (this.isSharedDevice()) return;

    const stored = this.readStoredValues();
    for (const field of this.fields) {
      if (isIdentityField(field) && stored[field.key] != null) {
        formGroup.controls[field.key].setValue(stored[field.key]);
      }
    }
  }

  private readStoredValues(): Record<string, unknown> {
    const history = this.storageService.get(StoredItem.OrderForm);
    return history ? JSON.parse(history) : {};
  }

  // Shared devices serve many guests one after another - an EzeTap terminal, or a JRCC table's
  // tablet - so one guest's name/phone/email must never be kept for the next.
  private isSharedDevice(): boolean {
    return !!this.dataService.ezetapMachineId() || this.isJrccTable();
  }

  private isJrccTable(): boolean {
    return this.constants.JRCC_TABLE_IDS.includes(this.dataService.table()?.id);
  }

  private resetForJrccTables(): void {
    if (this.isJrccTable()) {
      this.formGroup.reset();
    }
  }

  private checkRoomAndClose(): void {
    const restaurantId = this.dataService.restaurant().id;
    const roomControl = this.formGroup.controls['room'];
    this.loading = true;

    this.apiService.checkMulesoftRoom(restaurantId, roomControl.value)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: res => {
          if (res.valid) {
            this.close(this.toMetadata());
          } else {
            roomControl.setErrors({ backend: res.message });
          }
        },
        error: error => this.toastService.error(error.statusText),
      });
  }
}
